import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, TrendingUp, Clock, Users, Zap, CheckCircle2, Star, Target, Headphones } from 'lucide-react'
import { usePageSEO } from '@/hooks/usePageSEO'

export const Route = createFileRoute('/why-jeridschool')({
  component: WhyJeridSchoolPage,
})

function WhyJeridSchoolPage() {
  const { t } = useTranslation('service')
  usePageSEO({
    title: 'Pourquoi JeridSchool - Une plateforme adaptee aux ecoles tunisiennes',
    description:
      'Voyez pourquoi JeridSchool aide les etablissements en Tunisie a gagner du temps, centraliser les operations et mieux connecter parents et enseignants.',
    keywords:
      'pourquoi JeridSchool, logiciel scolaire Tunisie, gestion ecole privee, plateforme scolaire locale',
    path: '/why-jeridschool',
  })

  const benefits = [
    {
      icon: Zap,
      title: t('why-jeridschool.whatWeOffer.benefits.automation.title'),
      description: t('why-jeridschool.whatWeOffer.benefits.automation.description'),
      value: t('why-jeridschool.whatWeOffer.benefits.automation.value')
    },
    {
      icon: Users,
      title: t('why-jeridschool.whatWeOffer.benefits.unified.title'),
      description: t('why-jeridschool.whatWeOffer.benefits.unified.description'),
      value: t('why-jeridschool.whatWeOffer.benefits.unified.value')
    },
    {
      icon: TrendingUp,
      title: t('why-jeridschool.whatWeOffer.benefits.analytics.title'),
      description: t('why-jeridschool.whatWeOffer.benefits.analytics.description'),
      value: t('why-jeridschool.whatWeOffer.benefits.analytics.value')
    },
    {
      icon: Clock,
      title: t('why-jeridschool.whatWeOffer.benefits.workflow.title'),
      description: t('why-jeridschool.whatWeOffer.benefits.workflow.description'),
      value: t('why-jeridschool.whatWeOffer.benefits.workflow.value')
    },
    {
      icon: Target,
      title: t('why-jeridschool.whatWeOffer.benefits.improvement.title'),
      description: t('why-jeridschool.whatWeOffer.benefits.improvement.description'),
      value: t('why-jeridschool.whatWeOffer.benefits.improvement.value')
    },
    {
      icon: Headphones,
      title: t('why-jeridschool.whatWeOffer.benefits.support.title'),
      description: t('why-jeridschool.whatWeOffer.benefits.support.description'),
      value: t('why-jeridschool.whatWeOffer.benefits.support.value')
    }
  ]

  const whatYouMiss = [
    {
      title: t('why-jeridschool.valueSection.items.manual.title'),
      description: t('why-jeridschool.valueSection.items.manual.description')
    },
    {
      title: t('why-jeridschool.valueSection.items.fragmented.title'),
      description: t('why-jeridschool.valueSection.items.fragmented.description')
    },
    {
      title: t('why-jeridschool.valueSection.items.visibility.title'),
      description: t('why-jeridschool.valueSection.items.visibility.description')
    },
    {
      title: t('why-jeridschool.valueSection.items.scalability.title'),
      description: t('why-jeridschool.valueSection.items.scalability.description')
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-blue-100">
      <div className="container mx-auto px-4 py-12">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 mb-8 text-gray-600 hover:text-blue-600 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('why-jeridschool.backButton')}</span>
        </Link>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('why-jeridschool.hero.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('why-jeridschool.hero.description')}
          </p>
        </div>

       <div></div>
        {/* What We Offer */}
        <div className="mb-20 bg-primary rounded-2xl p-8 md:p-12 shadow-lg">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('why-jeridschool.whatWeOffer.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('why-jeridschool.whatWeOffer.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              // Alternate between yellow and blue for icons
              const isYellow = index % 2 === 0
              const iconBg = isYellow ? 'bg-yellow-100' : 'bg-blue-100'
              const iconColor = isYellow ? 'text-yellow-600' : 'text-blue-600'
              const valueColor = isYellow ? 'text-yellow-600' : 'text-blue-600'
              
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${iconBg}`}>
                    <benefit.icon className={`w-6 h-6 ${iconColor}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {benefit.description}
                  </p>
                  <div className={`flex items-center gap-2 text-sm font-semibold ${valueColor}`}>
                    <CheckCircle2 className="w-4 h-4" />
                    <span>{benefit.value}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* The Value of Choosing JeridSchool */}
        <div className="mb-20 bg-white rounded-2xl p-8 md:p-12 shadow-lg">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('why-jeridschool.valueSection.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('why-jeridschool.valueSection.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whatYouMiss.map((item, index) => {
              // Items 1 and 4 (index 0 and 3) = yellow, Items 2 and 3 (index 1 and 2) = blue
              const isYellow = index === 0 || index === 3
              const bgColor = isYellow ? 'bg-yellow-50' : 'bg-blue-50'
              const borderColor = isYellow ? 'border-yellow-200' : 'border-blue-200'
              const circleBg = isYellow ? 'bg-yellow-500' : 'bg-blue-500'
              
              return (
                <div
                  key={index}
                  className={`flex items-start gap-4 p-6 rounded-xl border ${borderColor} ${bgColor} shadow-sm`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${circleBg}`}>
                    <span className="text-sm font-bold text-white">{index + 1}</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {item.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Success Stories */}
     

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 md:p-12 text-center text-white">
          <Star className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl font-bold mb-4">
            {t('why-jeridschool.cta.title')}
          </h2>
          <p className="text-xl mb-8 opacity-95 max-w-2xl mx-auto">
            {t('why-jeridschool.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/join"
              className="px-8 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/20 hover:shadow-lg transition-all"
            >
              {t('why-jeridschool.cta.scheduleDemo')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
